import React, { useEffect, useMemo } from 'react';
import { FormikProps } from 'formik';
import { Box, CircularProgress } from '@material-ui/core';
import useAddControlsStyles
    from 'modules/clients/content/TimeAndExpensePage/SheetsInProgress/AddEntryControls/AddControlsStyles';
import UpperButton from 'shared/components/buttons/UpperButton';
import ScaZone from 'shared/components/formSpecialFields/scaZone/ScaZone';
import { EntryType, QuantityType } from 'shared/models/sheet/Sheet';
import FormField from 'shared/components/formFields/FormField';
import { activityHasCompletes } from 'shared/utils/formatters/activityFormatter';
import { selectIsInitialLoadingTimeAndExpense } from 'store/components/workingSheets/workingSheetsSelectors';
import { selectScaEnabled } from 'store/entities/clients/selectors/configurationSelectors';
import { IPayPeriod } from 'store/entities/timesheet/models/PayPeriod';
import { CommonEntryInlineFields, IBaseCommonEntryFieldsProps } from './CommonEntryInlineFields';
import { defaultValues, ITimeEntryFormValues } from './TimeEntryModel';
import { EntrySlug, InputFields } from 'store/entities/clients/clientsModel';
import { AccessTime } from '@material-ui/icons';
import { ICommonEntryFormValues } from './EntryCommonFields';
import TimeDataField from '../../formSpecialFields/TimeEntryField/TimeDataField';
import CompletesQuantity from 'shared/components/formFields/CompletesQuantity';
import { showField } from '../utils';
import { TimesheetSettings } from 'shared/models/JobNumber';
import { useJobNumberActivities } from 'shared/components/forms/entries/helpers/commonEntryFormHelpers';
import { selectCurrentClientId } from 'store/entities/clients/selectors/clientsSelectors';
import { useSelector } from 'react-redux';
import { entryFormGetAssignment } from 'shared/utils/helpers/entries';
import {
    selectAssignmentProjectIds,
    selectAssignmentsById,
    selectSubassignmentsByIds,
} from 'store/entities/configuration/configurationSelectors';

export interface IAddTimeEntryFormProps extends FormikProps<ITimeEntryFormValues>, IBaseCommonEntryFieldsProps {
    payPeriod: IPayPeriod;
    areaId?: number;
    inputs: InputFields;
    onChangeCommonValues: (values: ICommonEntryFormValues) => void;
    disabled?: boolean;
}

export function AddTimeEntryInlineForm({
    supervisorId,
    areaId,
    payPeriod,
    setFieldValue,
    setTouched,
    handleSubmit,
    inputs,
    onChangeCommonValues,
    userId,
    disabled = false,
    ...props
}: IAddTimeEntryFormProps) {
    const classes = useAddControlsStyles();
    const {
        projectAssignment,
        activity,
        notes,
        jobNumber,
        entry_date,
    } = props.values;

    const assignmentsById = useSelector(selectAssignmentsById);
    const assignmentProjectId = useSelector(selectAssignmentProjectIds);
    const clientId = useSelector(selectCurrentClientId);
    const subassignmentsByIds = useSelector(selectSubassignmentsByIds);
    const isLoading = useSelector(selectIsInitialLoadingTimeAndExpense);

    const assignmentId: string | undefined = useMemo(() => {
        if (projectAssignment?.assignment?.id) {
            return projectAssignment.assignment.id;
        }

        const { assignmentId: calculatedAssignmentId } = entryFormGetAssignment(
            userId, props.values, assignmentsById, assignmentProjectId, clientId, subassignmentsByIds,
        );

        return calculatedAssignmentId || undefined;
    }, [props.values, projectAssignment, assignmentsById, assignmentProjectId, clientId, subassignmentsByIds, userId]);

    const scaEnabled = useSelector(selectScaEnabled);

    useEffect(() => {
        setFieldValue('scaZone', defaultValues.scaZone);
    }, [projectAssignment?.sca_zone_id, setFieldValue]);
    const jobNumberActivities = useJobNumberActivities(EntryType.TIME, jobNumber);
    useEffect(() => {
        if (jobNumber && !jobNumberActivities?.includes(activity?.id || '')){
            setFieldValue('activity', defaultValues.activity);
        }
    }, [jobNumber, jobNumberActivities, activity, setFieldValue]);

    const activityNotSelectedYet = !activity;

    const isDataFieldVisibleByOtherFields = useMemo(() => (
        entry_date && !activityNotSelectedYet
    ), [entry_date, activityNotSelectedYet]);

    const quantityType = useMemo(() => {
        if (jobNumber) {
            return jobNumber.timesheet_setting === TimesheetSettings.PerFile
                ? QuantityType.FILE
                : activity?.data_type;
        }
        return activity?.data_type;
    }, [activity, jobNumber]);

    return (
        <form onSubmit={handleSubmit} className={classes.form}>
            <Box className={classes.controlsContainer}>
                <Box className={classes.aSide}>
                    <CommonEntryInlineFields
                        values={props.values}
                        onChangeCommonValues={onChangeCommonValues}
                        setFieldValue={setFieldValue}
                        setTouched={setTouched}
                        payPeriod={payPeriod}
                        areaId={areaId}
                        entryType={EntryType.TIME}
                        inputs={inputs}
                        supervisorId={supervisorId}
                        userId={userId}
                        disabled={disabled}
                    />

                    {isDataFieldVisibleByOtherFields && (
                        <TimeDataField
                            name="data"
                            quantityType={quantityType}
                            className={classes.timeInputField}
                            totalAmountClassName={classes.timeTotalInputField}
                            disabled={disabled}
                        />
                    )}

                    {showField(inputs, EntrySlug.Notes) && (
                        <>
                            <FormField
                                name="notes"
                                label={activityHasCompletes(activity) ? 'Case Numbers' : 'Notes..'}
                                className={classes.fixedLarge}
                                disabled={disabled}
                            />
                            {activityHasCompletes(activity) && (
                                <CompletesQuantity
                                    name="completes"
                                    label="Cmpl."
                                    className={classes.selectFieldSmall}
                                    key={notes}
                                    notes={notes}
                                />
                            )}
                        </>
                    )}

                    {assignmentId && scaEnabled && (
                        <ScaZone
                            name="scaZone"
                            label="SCA Zone"
                            parentScaZoneId={projectAssignment?.sca_zone_id || undefined}
                            className={classes.fixedLarge}
                            assignmentId={assignmentId}
                            disabled={disabled}
                        />
                    )}
                    <UpperButton
                        label={(
                            <>
                                <span>+ Add</span>
                                {isLoading ? (
                                    <Box className={classes.loaderIconContainer}>
                                        <CircularProgress className={classes.loaderIcon} />
                                    </Box>
                                ) : (
                                    <AccessTime className={classes.addIcon} />
                                )}
                            </>
                        )}
                        className={classes.upperButton}
                        disabled={disabled || isLoading}
                    />
                </Box>
            </Box>
        </form>
    );
}
